import { useState, useCallback, useEffect, useRef } from 'react';
import { metaApi, MetaEmbeddedSignupConfig, MetaEmbeddedSignupResult } from '../services/metaApi';

// Facebook SDK types are defined in @/types/window.d.ts

/**
 * State for the Embedded Signup flow
 */
interface EmbeddedSignupState {
  isLoading: boolean;
  isSDKLoaded: boolean;
  isSDKInitialized: boolean;
  error: string | null;
  result: MetaEmbeddedSignupResult | null;
}

/**
 * PostMessage event data from WhatsApp Embedded Signup
 *
 * Eventos de sucesso variam por fluxo (Meta docs — Embedded Signup › Implementation):
 * - FINISH: fluxo Cloud API padrão (standalone)
 * - FINISH_ONLY_WABA: usuário concluiu sem registrar número (só WABA criada)
 * - FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING: fluxo de coexistência (Business App)
 */
interface EmbeddedSignupMessageEvent {
  type: 'WA_EMBEDDED_SIGNUP';
  event:
    | 'FINISH'
    | 'FINISH_ONLY_WABA'
    | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
    | 'CANCEL'
    | 'ERROR';
  data?: {
    phone_number_id?: string;
    waba_id?: string;
    business_id?: string;
  };
}

/**
 * Hook for WhatsApp Embedded Signup via Meta Platform
 *
 * Handles:
 * 1. Loading Facebook SDK dynamically
 * 2. Initializing SDK with config from backend
 * 3. Starting FB.login() with Embedded Signup config
 * 4. Processing PostMessage events from Meta popup
 * 5. Completing signup flow with backend
 *
 * @example
 * ```tsx
 * const { startEmbeddedSignup, state, isReady } = useMetaEmbeddedSignup();
 *
 * if (isReady) {
 *   <Button onClick={startEmbeddedSignup}>Login com Facebook</Button>
 * }
 *
 * if (state.result) {
 *   // Use result.accessToken, result.phoneNumberId, result.wabaId
 * }
 * ```
 */
export function useMetaEmbeddedSignup() {
  const [state, setState] = useState<EmbeddedSignupState>({
    isLoading: false,
    isSDKLoaded: false,
    isSDKInitialized: false,
    error: null,
    result: null,
  });

  const configRef = useRef<MetaEmbeddedSignupConfig | null>(null);
  const pendingCodeRef = useRef<string | null>(null);

  // 🔧 FIX: Refs para armazenar dados do PostMessage em caso de race condition
  // PostMessage pode chegar ANTES do FB.login callback completar
  const pendingPhoneNumberIdRef = useRef<string | null>(null);
  const pendingWabaIdRef = useRef<string | null>(null);
  const pendingModeRef = useRef<'standalone' | 'coexistence' | null>(null);

  /**
   * Load Facebook SDK script dynamically
   */
  const loadSDK = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.FB) {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
        return;
      }

      // Check if script already exists
      if (document.getElementById('facebook-jssdk')) {
        // SDK script exists but FB not available yet - wait for it
        const checkFB = setInterval(() => {
          if (window.FB) {
            clearInterval(checkFB);
            setState(prev => ({ ...prev, isSDKLoaded: true }));
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkFB);
          reject(new Error('Facebook SDK load timeout'));
        }, 10000);
        return;
      }

      // Load SDK script
      window.fbAsyncInit = () => {
        setState(prev => ({ ...prev, isSDKLoaded: true }));
        resolve();
      };

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      script.onerror = () => {
        reject(new Error('Failed to load Facebook SDK'));
      };

      document.body.appendChild(script);
    });
  }, []);

  /**
   * Initialize Facebook SDK with config from backend
   */
  const initializeSDK = useCallback(async (): Promise<MetaEmbeddedSignupConfig> => {
    if (!window.FB) {
      throw new Error('Facebook SDK not loaded');
    }

    // Get config from backend
    const config = await metaApi.getEmbeddedSignupConfig();
    configRef.current = config;

    // Initialize FB SDK
    window.FB.init({
      appId: config.appId,
      cookie: true,
      xfbml: true,
      version: config.graphApiVersion,
    });

    setState(prev => ({ ...prev, isSDKInitialized: true }));
    console.log('[Meta Embedded Signup] SDK initialized with config:', {
      appId: config.appId,
      version: config.graphApiVersion,
    });

    return config;
  }, []);

  /**
   * Handle PostMessage events from WhatsApp Embedded Signup popup
   */
  const handlePostMessage = useCallback((event: MessageEvent) => {
    // Validate origin - Meta sends from facebook.com domain
    if (!event.origin.includes('facebook.com')) {
      return;
    }

    let data: EmbeddedSignupMessageEvent;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return; // Not a valid JSON message
    }

    // Check if this is a WhatsApp Embedded Signup message
    if (data.type !== 'WA_EMBEDDED_SIGNUP') {
      return;
    }

    // 🔍 DEBUG: Log completo para análise
    console.log('[Meta Embedded Signup] PostMessage received - RAW data:', JSON.stringify(data, null, 2));
    console.log('[Meta Embedded Signup] PostMessage event.data type:', typeof event.data);
    console.log('[Meta Embedded Signup] Event type:', data.event);
    console.log('[Meta Embedded Signup] Data object:', data.data);
    console.log('[Meta Embedded Signup] Pending code ref:', pendingCodeRef.current);

    switch (data.event) {
      // Todos os FINISH_* são sucesso — o de coexistência é FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING
      // (bug histórico 2026-07-03: só FINISH era tratado; coexistência caía fora do switch em silêncio)
      case 'FINISH':
      case 'FINISH_ONLY_WABA':
      case 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING':
        // 🔍 DEBUG: Log de todos os campos recebidos
        console.log('[Meta Embedded Signup] FINISH event - Checking fields:');
        console.log('  - data.data:', data.data);
        console.log('  - data.data?.phone_number_id:', data.data?.phone_number_id);
        console.log('  - data.data?.waba_id:', data.data?.waba_id);

        // 🔍 DEBUG: Log de campos alternativos que o Meta pode estar usando
        const anyData = data as any;
        console.log('  - Campos alternativos no data:');
        console.log('    - phoneNumberId:', anyData.data?.phoneNumberId);
        console.log('    - PhoneNumberId:', anyData.data?.PhoneNumberId);
        console.log('    - wabaId:', anyData.data?.wabaId);
        console.log('    - WabaId:', anyData.data?.WabaId);
        console.log('    - businessId:', anyData.data?.businessId);
        console.log('    - BusinessId:', anyData.data?.BusinessId);
        console.log('    - business_id:', anyData.data?.business_id);
        console.log('  - Todas as chaves do data.data:', data.data ? Object.keys(data.data) : 'undefined');

        // Tentar extrair os dados de várias formas possíveis
        const phoneNumberId = data.data?.phone_number_id
          || anyData.data?.phoneNumberId
          || anyData.data?.PhoneNumberId
          || anyData.phoneNumberId
          || anyData.PhoneNumberId;

        const wabaId = data.data?.waba_id
          || anyData.data?.wabaId
          || anyData.data?.WabaId
          || anyData.data?.businessId
          || anyData.data?.BusinessId
          || anyData.data?.business_id
          || anyData.wabaId
          || anyData.WabaId;

        console.log('[Meta Embedded Signup] Extracted values:');
        console.log('  - phoneNumberId:', phoneNumberId);
        console.log('  - wabaId:', wabaId);
        console.log('  - code (from ref):', pendingCodeRef.current);

        // User completed the signup flow
        if (phoneNumberId && wabaId) {
          // 🔧 FIX: Armazenar dados nos refs para caso de race condition
          pendingPhoneNumberIdRef.current = phoneNumberId;
          pendingWabaIdRef.current = wabaId;
          console.log('[Meta Embedded Signup] Stored PostMessage data in refs');

          // Verificar se o código já está disponível (FB.login callback já executou)
          if (pendingCodeRef.current) {
            console.log('[Meta Embedded Signup] Code already available, completing signup...');
            handleSignupComplete(
              pendingCodeRef.current,
              phoneNumberId,
              wabaId
            );
          } else {
            // 🔧 FIX: Código ainda não disponível - FB.login callback vai completar depois
            console.log('[Meta Embedded Signup] Code not yet available - waiting for FB.login callback');
            // NÃO mostrar erro! Apenas aguardar o FB.login callback
          }
        } else {
          console.error('[Meta Embedded Signup] ERROR: Missing data fields');
          console.error('  - phoneNumberId:', phoneNumberId);
          console.error('  - wabaId:', wabaId);
          setState(prev => ({
            ...prev,
            isLoading: false,
            error: `Dados incompletos recebidos do WhatsApp Embedded Signup. phoneNumberId: ${phoneNumberId || 'vazio'}, wabaId: ${wabaId || 'vazio'}`,
          }));
        }
        break;

      case 'CANCEL':
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Processo cancelado pelo usuário',
        }));
        break;

      case 'ERROR':
        console.error('[Meta Embedded Signup] ERROR event received:', data);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Erro durante o processo de configuração do WhatsApp',
        }));
        break;

      default:
        // Evento novo/desconhecido da Meta — logar alto em vez de falhar em silêncio
        console.warn('[Meta Embedded Signup] Unhandled WA_EMBEDDED_SIGNUP event:', data.event, data);
        break;
    }
  }, []);

  /**
   * Complete the signup flow by sending data to backend
   */
  const handleSignupComplete = async (
    code: string,
    phoneNumberId: string,
    wabaId: string
  ) => {
    try {
      const mode = pendingModeRef.current ?? 'standalone';
      console.log('[Meta Embedded Signup] Completing signup with backend:', { phoneNumberId, wabaId, mode });

      const result = await metaApi.completeEmbeddedSignup({ code, phoneNumberId, wabaId, mode });

      setState(prev => ({
        ...prev,
        isLoading: false,
        result,
        error: null,
      }));

      console.log('[Meta Embedded Signup] Signup completed successfully:', {
        mode: result.mode,
        accountMode: result.accountMode,
        webhookConfigured: result.webhookConfigured,
      });
    } catch (error: any) {
      console.error('[Meta Embedded Signup] Failed to complete signup:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.message || error.message || 'Erro ao finalizar configuração',
      }));
    }
  };

  /**
   * Start the Embedded Signup flow
   */
  const startEmbeddedSignup = useCallback(async (options?: { mode?: 'standalone' | 'coexistence' }) => {
    const mode = options?.mode ?? 'standalone';
    setState(prev => ({ ...prev, isLoading: true, error: null, result: null }));

    try {
      // Step 1: Load SDK if needed
      if (!state.isSDKLoaded) {
        await loadSDK();
      }

      // Step 2: Initialize SDK with config from backend
      let config = configRef.current;
      if (!state.isSDKInitialized || !config) {
        config = await initializeSDK();
      }

      // Step 3: featureType determina o fluxo (standalone vs coexistência).
      // Meta usa o MESMO config_id pros dois — ver Meta docs Etapa 2:
      // https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows/onboarding-business-app-users/
      const featureType = mode === 'coexistence' ? 'whatsapp_business_app_onboarding' : 'whatsapp_embedded_signup';

      // Store mode for use in complete step
      pendingModeRef.current = mode;

      // Step 4: Add PostMessage listener
      window.addEventListener('message', handlePostMessage);

      console.log('[Meta Embedded Signup] Starting FB.login()', { mode, featureType, configId: config.configId });

      if (!window.FB) {
        throw new Error('Facebook SDK not available');
      }

      window.FB.login(
        (response: FBLoginResponse) => {
          console.log('[Meta Embedded Signup] FB.login response:', {
            status: response.status,
            hasCode: !!response.authResponse?.code,
          });

          if (response.authResponse?.code) {
            pendingCodeRef.current = response.authResponse.code;

            if (pendingPhoneNumberIdRef.current && pendingWabaIdRef.current) {
              handleSignupComplete(
                response.authResponse.code,
                pendingPhoneNumberIdRef.current,
                pendingWabaIdRef.current
              );
            }
          } else {
            setState(prev => ({
              ...prev,
              isLoading: false,
              error: response.status === 'not_authorized'
                ? 'Autorização negada pelo usuário'
                : 'Não foi possível obter autorização do Facebook',
            }));
            window.removeEventListener('message', handlePostMessage);
          }
        },
        {
          config_id: config.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            sessionInfoVersion: '3',
            featureType,
            setup: {},
          },
        }
      );
    } catch (error: any) {
      console.error('[Meta Embedded Signup] Error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Erro ao iniciar processo de login',
      }));
    }
  }, [state.isSDKLoaded, state.isSDKInitialized, loadSDK, initializeSDK, handlePostMessage]);

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      isSDKLoaded: state.isSDKLoaded, // Keep SDK loaded state
      isSDKInitialized: state.isSDKInitialized, // Keep SDK initialized state
      error: null,
      result: null,
    });
    pendingCodeRef.current = null;
    // 🔧 FIX: Limpar também os refs do PostMessage (race condition fix)
    pendingPhoneNumberIdRef.current = null;
    pendingWabaIdRef.current = null;
    pendingModeRef.current = null;
  }, [state.isSDKLoaded, state.isSDKInitialized]);

  /**
   * Cleanup PostMessage listener on unmount
   */
  useEffect(() => {
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [handlePostMessage]);

  return {
    // State
    state,
    isReady: !state.isLoading && !state.error,
    isLoading: state.isLoading,
    error: state.error,
    result: state.result,

    // Actions
    startEmbeddedSignup,
    reset,

    // SDK status
    isSDKLoaded: state.isSDKLoaded,
    isSDKInitialized: state.isSDKInitialized,
  };
}
